import { createAsyncThunk } from "@reduxjs/toolkit"
import { toast } from "react-toastify"
import apiCall from "../../backend/http"

export const handleApiError = (error) => {
  let errorMessage = "An error occurred while processing your request"
  let errorType = "GENERAL_ERROR"
  let errorDetails = {}

  if (error.response) {
    const responseData = error.response.data

    if (typeof responseData === "string") {
      if (responseData.includes("IntegrityError")) {
        if (responseData.includes("invoice_number")) {
          return {
            message:
              "A booking with this invoice number already exists. Please try again.",
            type: "DUPLICATE_INVOICE",
            status: error.response.status,
            data: responseData,
            details: {
              field: "invoice_number",
              constraint: "unique"
            }
          }
        }
        if (responseData.includes("vehicle_availability")) {
          return {
            message:
              "The selected vehicle is no longer available for the chosen dates.",
            type: "VEHICLE_UNAVAILABLE",
            status: error.response.status,
            data: responseData,
            details: {
              field: "vehicle_booking",
              constraint: "availability"
            }
          }
        }
      }
    }

    if (responseData?.errors) {
      const fieldErrors = Object.entries(responseData.errors)
        .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
        .join("; ")
      errorMessage = fieldErrors
      errorType = "VALIDATION_ERROR"
      errorDetails = responseData.errors
    } else {
      switch (error.response.status) {
        case 400:
          errorType = "BAD_REQUEST"
          errorMessage =
            responseData?.message ||
            "Invalid request data. Please check your input."
          break
        case 401:
          errorType = "UNAUTHORIZED"
          errorMessage = "Your session has expired. Please log in again."
          break
        case 403:
          errorType = "FORBIDDEN"
          errorMessage = "You do not have permission to perform this action."
          break
        case 404:
          errorType = "NOT_FOUND"
          errorMessage = "Token Expired."
          break
        case 409:
          errorType = "CONFLICT"
          errorMessage =
            responseData?.message ||
            "A conflict occurred with the current state."
          break
        case 422:
          errorType = "VALIDATION_ERROR"
          errorMessage =
            responseData?.message || "The provided data is invalid."
          errorDetails = responseData?.errors || {}
          break
        case 429:
          errorType = "RATE_LIMIT"
          errorMessage = "Too many requests. Please try again later."
          break
        case 500:
        case 502:
        case 503:
          errorType = "SERVER_ERROR"
          errorMessage = "A server error occurred. Please try again later."
          break
        default:
          errorType = "UNKNOWN_ERROR"
          errorMessage =
            responseData?.message || "An unexpected error occurred."
      }
    }
  } else if (error.request) {
    errorType = "NETWORK_ERROR"
    errorMessage =
      "Unable to connect to the server. Please check your internet connection."
  }

  return {
    message: errorMessage,
    type: errorType,
    status: error.response?.status || 500,
    data: error.response?.data || {},
    details: errorDetails
  }
}

const bookingRequest = createAsyncThunk(
  "booking/request",
  async (postData, { rejectWithValue }) => {
    try {
      const formatDateTime = (dateString) => {
        try {
          const date = new Date(dateString)
          if (isNaN(date.getTime())) return null

          const pad = (num) => String(num).padStart(2, "0")
          return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
          )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
            date.getSeconds()
          )}`
        } catch (error) {
          console.error("Date formatting error:", error)
          return null
        }
      }

      const transformedData = {
        user: postData.user,
        service_type: postData.service_type,
        client_name: postData.client_name?.trim(),
        pickup_location: postData.pickup_location?.trim() || "",
        dropoff_location: postData.dropoff_location?.trim() || "",
        message: postData.message?.trim() || "",
        billing_type: postData.billing_type,
        email: postData.email?.trim(),
        phone: postData.phone?.trim()?.replace(/\D/g, ""),
        additional_notes: postData.additional_notes?.trim() || "",
        vehicle_bookings: postData.vehicle_bookings?.map((booking) => ({
          vehicle: booking.vehicle,
          date_ranges: booking.date_ranges?.map((range) => {
            const startDate = formatDateTime(range.start_date)
            const endDate = formatDateTime(range.end_date)

            if (!startDate || !endDate) {
              throw new Error("Invalid date format in booking")
            }

            return {
              start_date: startDate,
              end_date: endDate,
              quantity: parseInt(range.quantity) || 1
            }
          })
        }))
      }

      if (postData.billing_type === "CORPORATE") {
        transformedData.company_name = postData.company_name?.trim()
        transformedData.company_registration =
          postData.company_registration?.trim()
        transformedData.tax_id = postData.tax_id?.trim()
      }

      const response = await apiCall.post(
        "/api/bookings/requests/",
        transformedData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          }
        }
      )

      return response.data
    } catch (error) {
      if (error.response) {
        console.error("Error response data:", error.response.data)
        console.error("Error response status:", error.response.status)
        console.error("Error response headers:", error.response.headers)
      }

      if (error.message === "Invalid date format in booking") {
        return rejectWithValue({
          type: "VALIDATION_ERROR",
          message: "Please check your booking dates and try again."
        })
      }
      if (error.response?.status === 500) {
        return rejectWithValue({
          type: "SERVER_ERROR",
          message: "The server encountered an error. Please try again later.",
          details: error.response.data
        })
      }

      if (error.request && !error.response) {
        return rejectWithValue({
          type: "NETWORK_ERROR",
          message:
            "Unable to connect to the server. Please check your internet connection."
        })
      }

      return rejectWithValue({
        type: "UNKNOWN_ERROR",
        message: error.message || "An unexpected error occurred"
      })
    }
  }
)

export const updateBooking = createAsyncThunk(
  "booking/update",
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const currentBookingResponse = await apiCall.get(
        `/api/bookings/requests/${id}/`
      )
      const currentBooking = currentBookingResponse.data

      const completeUpdateData = {
        ...currentBooking,
        ...updateData
      }

      const response = await apiCall.put(
        `/api/bookings/requests/${id}/`,
        completeUpdateData
      )

      return response.data
    } catch (error) {
      console.error("🚫 Update Error:", {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      })
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const patchBooking = createAsyncThunk(
  "booking/patch",
  async ({ id, patchData }, { rejectWithValue, getState }) => {
    try {
      const { data } = await apiCall.patch(
        `/api/bookings/requests/${id}/`,
        patchData
      )

      if (
        patchData.booking_status &&
        data.booking_status === patchData.booking_status
      ) {
        console.log("✅ Status successfully updated:", {
          status: data.booking_status
        })
      }

      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const deleteBooking = createAsyncThunk(
  "booking/delete",
  async (id, { rejectWithValue }) => {
    try {
      await apiCall.delete(`/api/bookings/requests/${id}/`)
      toast.success("Booking deleted successfully")
      return id
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const fetchBookings = createAsyncThunk(
  "booking/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.get("/api/bookings/requests/")
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const fetchBookingById = createAsyncThunk(
  "booking/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.get(`/api/bookings/requests/${id}/`)
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const confirmBooking = createAsyncThunk(
  "booking/confirm",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.post(
        `/api/bookings/requests/${id}/confirm/`
      )
      toast.success("Booking confirmed successfully")
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const cancelBooking = createAsyncThunk(
  "booking/cancel",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.post(
        `/api/bookings/requests/${id}/cancel/`
      )
      toast.success("Booking cancelled successfully")
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const createPricelist = createAsyncThunk(
  "pricelist/create",
  async (pricelistData, { rejectWithValue }) => {
    try {
      const formattedData = {
        ...pricelistData,
        name: pricelistData.name?.trim(),
        description: pricelistData.description?.trim() || "",
        items: pricelistData.items?.map((item) => ({
          ...item,
          name: item.name?.trim(),
          price: Number(item.price),
          description: item.description?.trim() || ""
        }))
      }

      const { data } = await apiCall.post(
        "/api/pricelist/pricelists/",
        formattedData
      )

      toast.success("Price list created successfully")
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const fetchPricelists = createAsyncThunk(
  "pricelist/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.get("/api/pricelist/pricelists/")
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const fetchPricelistById = createAsyncThunk(
  "pricelist/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.get(`/api/pricelist/pricelists/${id}/`)
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const updatePricelist = createAsyncThunk(
  "pricelist/update",
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.put(
        `/api/pricelist/pricelists/${id}/`,
        updateData
      )

      toast.success("Price list updated successfully")
      return data
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const deletePricelist = createAsyncThunk(
  "pricelist/delete",
  async (id, { rejectWithValue }) => {
    try {
      await apiCall.delete(`/api/pricelist/pricelists/${id}/`)
      toast.success("Price list deleted successfully")
      return id
    } catch (error) {
      const errorData = handleApiError(error)
      toast.error(errorData.message)
      return rejectWithValue(errorData)
    }
  }
)

export const validatePricelistData = (pricelistData) => {
  const errors = []

  if (!pricelistData.name?.trim()) {
    errors.push("Price list name is required")
  }

  if (!Array.isArray(pricelistData.items) || !pricelistData.items.length) {
    errors.push("At least one item is required in the price list")
  } else {
    pricelistData.items.forEach((item, index) => {
      if (!item.name?.trim()) {
        errors.push(`Item name is required for item ${index + 1}`)
      }
      if (!item.price || isNaN(Number(item.price))) {
        errors.push(`Valid price is required for item ${index + 1}`)
      }
    })
  }

  return errors
}

export const updateBookingStatus = createAsyncThunk(
  "booking/updateBookingStatus",
  async ({ bookingId, newStatus, currentStatus }, { rejectWithValue }) => {
    console.log("Booking Status Update Detailed Attempt:", {
      bookingId,
      newStatus,
      currentStatus,
      fullPayload: {
        status: newStatus,
        current_status: currentStatus,
        booking_id: bookingId
      }
    })

    try {
      const { data } = await apiCall.post(
        `/api/bookings/requests/${bookingId}/update-booking-status/`,
        {
          status: newStatus,
          current_status: currentStatus,
          booking_id: bookingId
        }
      )
      return { bookingId, data }
    } catch (error) {
      console.error("COMPREHENSIVE Error Breakdown:", {
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        errorMessage: error.message,
        sentPayload: {
          status: newStatus,
          current_status: currentStatus,
          booking_id: bookingId
        }
      })
      return rejectWithValue(error.response?.data)
    }
  }
)

export const updateConfirmationStatus = createAsyncThunk(
  "booking/updateConfirmationStatus",
  async ({ bookingId, newStatus }, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.post(
        `/api/bookings/requests/${bookingId}/update-confirmation-status/`,
        {
          status: newStatus
        }
      )

      return {
        bookingId,
        data
      }
    } catch (error) {
      console.error("Confirmation status update failed:", {
        bookingId,
        newStatus,
        error: error.response?.data || error.message
      })
      const errorData = handleApiError(error)
      return rejectWithValue(errorData)
    }
  }
)

export const updateDocumentStatus = createAsyncThunk(
  "booking/updateDocumentStatus",
  async ({ bookingId, newStatus }, { rejectWithValue }) => {
    try {
      const { data } = await apiCall.post(
        `/api/bookings/requests/${bookingId}/update-document-status/`,
        {
          status: newStatus
        }
      )

      return {
        bookingId,
        data
      }
    } catch (error) {
      console.error("Document status update failed:", {
        bookingId,
        newStatus,
        error: error.response?.data || error.message
      })
      const errorData = handleApiError(error)
      return rejectWithValue(errorData)
    }
  }
)

export const fetchAvailableStatusTransitions = createAsyncThunk(
  "booking/fetchStatusTransitions",
  async (bookingId, { rejectWithValue }) => {
    if (!bookingId) {
      return rejectWithValue({
        message: "Booking ID is required",
        type: "VALIDATION_ERROR",
        status: 400
      })
    }

    try {
      const { data } = await apiCall.get(
        `/api/bookings/requests/${bookingId}/available-status-transitions/`
      )

      return {
        bookingId,
        transitions: data
      }
    } catch (error) {
      const errorData = handleApiError(error)
      console.error("Error fetching status transitions:", errorData)
      return rejectWithValue(errorData)
    }
  },
  {
    condition: (bookingId, { getState }) => {
      const { booking } = getState().app
      if (!bookingId) return false
      return !booking.statusTransitions[bookingId]
    }
  }
)

export { bookingRequest }
